import Image from '@atoms/Image/Image';
import ReturnLink from '@atoms/ReturnLink/ReturnLink';
import { IProductDetails } from '@models/IProductDetails';
import PaginationCarousel from '@molecules/PaginationCarousel/PaginationCarousel';
import ProductDetailsSidePanel from '@molecules/ProductDetailsSidePanel/ProductDetailsSidePanel';
import ProductDetailsTitle from '@molecules/ProductDetailsTitle/ProductDetailsTitle';
import classNames from 'classnames';
import { SwiperSlide } from 'swiper/react';

const ProductDetails = ({
  addToCartAddedLabel,
  addToCartErrorLabel,
  backOrderText,
  backgroundImage,
  basketEndpoint,
  basketLink,
  buyNowLabel,
  buyNowUrl,
  code,
  colours,
  coloursLabel,
  commercialText,
  copyTaxLabel,
  dealerFindLink,
  deliveryInformation,
  disableFindDealerCta,
  ecoTaxLabel,
  hidePriceFromSavePill,
  isRefurbished,
  maxBasketQuantityText,
  maxBasketValueText,
  maxProductQuantityText,
  mixedCartText,
  name,
  notifyMeContactFormEndpoint,
  overviewLink,
  preOrderText,
  pricingData,
  primarySkuCode,
  productImages,
  productsSubTitle,
  productsTitle,
  refurbishedIcon,
  refurbishedInformation,
  refurbishedLabel,
  regionalTaxLabel,
  savePriceLabel,
  shareOptions,
  skuCode,
  skuLabel,
  statusLabels,
  usbCharger,
  usbChargerLabel,
  variations,
  variationsLabel,
}: IProductDetails) => {
  const isBackgroundImagePortrait =
    !!backgroundImage?.largeDesktop?.height &&
    !!backgroundImage.largeDesktop.width &&
    backgroundImage.largeDesktop.height > backgroundImage.largeDesktop.width;

  return (
    <section
      className="bg-white py-9 text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-15-Product-Details"
    >
      <div className="container mt-10 xl:grid xl:grid-cols-12">
        <div className="order-first mb-10 flex w-full flex-row items-center justify-between xl:col-span-full xl:mb-0">
          {overviewLink?.name && overviewLink?.url && (
            <ReturnLink
              link={overviewLink}
              dataTestId="CT-15-Product-Details-Return-Link"
            />
          )}
          {skuLabel && skuCode && (
            <div
              className="body-four whitespace-nowrap text-right text-grey-450 dark:text-grey-500 xl:hidden"
              data-testid="CT-15-Product-Details-SKU-Label/Code"
            >
              {skuLabel}
              {skuCode}
            </div>
          )}
        </div>
        <span className="relative z-10 xl:hidden">
          <ProductDetailsTitle
            hidePriceFromSavePill={hidePriceFromSavePill}
            isRefurbished={isRefurbished}
            refurbishedIcon={refurbishedIcon}
            refurbishedLabel={refurbishedLabel}
            savePriceLabel={savePriceLabel}
            skuLabel={skuLabel}
            skuCode={skuCode}
            name={name}
            amountSaved={pricingData?.amountSaved}
            formattedAmountSaved={pricingData?.formattedAmountSaved}
            dataTestId="CT-15-Product-Details"
          />
        </span>

        <div className="relative mb-13 md:mb-0 xl:order-first xl:col-span-7 xl:col-start-1 xl:row-span-2">
          {backgroundImage && (
            <div className="absolute left-0 top-0 flex h-full w-full items-center md:items-start">
              <Image
                image={backgroundImage}
                classes={classNames('md:py-10 object-cover sticky top-0', {
                  'w-1/2': isBackgroundImagePortrait,
                  'w-full': !isBackgroundImagePortrait,
                })}
                dataTestId="CT-15-Product-Details-Background-Image"
              />
            </div>
          )}

          {!!productImages?.length && (
            <>
              <div className="relative hidden xl:block">
                <div className="flex w-full flex-col gap-52 px-13 py-15">
                  {productImages
                    .filter((image) => image)
                    .map((image, index) => (
                      <Image
                        key={`product-image-column-${image.desktop?.url}`}
                        image={image}
                        imageClasses="object-contain mx-auto p-15 max-h-[700px]"
                        sizes="(min-width: 1024px) 1024px, 100vw"
                        dataTestId={`CT-15-Product-Details-Product-Image-${index}`}
                      />
                    ))}
                </div>
              </div>
              <div className="relative block xl:hidden">
                <PaginationCarousel
                  id="product-details-images"
                  paginationClasses="mt-2"
                >
                  {productImages
                    .filter((image) => image)
                    .map((image, index) => (
                      <SwiperSlide
                        key={`product-details-image-${image.desktop?.url}`}
                        className="stretch-card my-auto"
                      >
                        <Image
                          image={image}
                          imageClasses="w-full my-auto object-cover"
                          dataTestId={`CT-15-Product-Details-Product-Image-${index}`}
                        />
                      </SwiperSlide>
                    ))}
                </PaginationCarousel>
              </div>
            </>
          )}
        </div>

        <ProductDetailsSidePanel
          addToCartAddedLabel={addToCartAddedLabel}
          addToCartErrorLabel={addToCartErrorLabel}
          backOrderText={backOrderText}
          basketEndpoint={basketEndpoint}
          basketLink={basketLink}
          buyNowLabel={buyNowLabel}
          buyNowUrl={buyNowUrl}
          code={code}
          colours={colours}
          coloursLabel={coloursLabel}
          commercialText={commercialText}
          copyTaxLabel={copyTaxLabel}
          dataTestId="CT-15-Product-Details-Side-Panel"
          dealerFindLink={dealerFindLink}
          deliveryInformation={deliveryInformation}
          disableFindDealerCta={disableFindDealerCta}
          ecoTaxLabel={ecoTaxLabel}
          hidePriceFromSavePill={hidePriceFromSavePill}
          isRefurbished={isRefurbished}
          maxBasketQuantityText={maxBasketQuantityText}
          maxBasketValueText={maxBasketValueText}
          maxProductQuantityText={maxProductQuantityText}
          mixedCartText={mixedCartText}
          name={name}
          notifyMeContactFormEndpoint={notifyMeContactFormEndpoint}
          preOrderText={preOrderText}
          pricingData={pricingData}
          primarySkuCode={primarySkuCode}
          productsSubTitle={productsSubTitle}
          productsTitle={productsTitle}
          refurbishedIcon={refurbishedIcon}
          refurbishedInformation={refurbishedInformation}
          refurbishedLabel={refurbishedLabel}
          regionalTaxLabel={regionalTaxLabel}
          savePriceLabel={savePriceLabel}
          shareOptions={shareOptions}
          skuCode={skuCode}
          skuLabel={skuLabel}
          statusLabels={statusLabels}
          usbCharger={usbCharger}
          usbChargerLabel={usbChargerLabel}
          variations={variations}
          variationsLabel={variationsLabel}
        />
      </div>
    </section>
  );
};

export default ProductDetails;
